import { QueryClient } from "@tanstack/react-query";
import { GenderApiValues, GenderMappingToApi, GenderMappingToUi, GenderUiValues } from "@utils/constants";
import { convertHeadersToObject } from "@utils/utils";
import { RequestDocument } from "graphql-request";
import { Link } from "react-router-dom";

import { BreadcrumbLink, MultiSelectItem, SelectItem } from "@fronterahealth/frontera-ui-components";

import { FronteraGraphQLClient } from "@api/graphql/graphql-service";
import {
  ApiLearnerMetadataGenderChoices,
  ApiProviderMetadataGenderChoices,
  ApiReportClientDetailsGenderChoices,
  ApiReportTemplatePayorTypeChoices,
  ApiReportTemplateStatusChoices,
  LearnerFileByIdDocument,
  ReportTemplateTypeEdge,
} from "@api/graphql/types-and-hooks";

export const getGenderValue = (
  value:
    | GenderApiValues
    | null
    | undefined
    | ApiProviderMetadataGenderChoices
    | ApiLearnerMetadataGenderChoices
    | ApiReportClientDetailsGenderChoices,
) => {
  const genderValue = value === "M" || value === "F" ? value : value?.toLowerCase();
  if (genderValue) return GenderMappingToUi[genderValue as GenderApiValues];

  return null;
};

export const setGenderValue = (value: GenderUiValues | null | undefined | string) => {
  if (value) return GenderMappingToApi[value as GenderUiValues];
  return null;
};

const capitalizeString = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
};

/**
 * Checks if a string looks like a base64 encoded ID rather than a regular word.
 * Base64 IDs typically contain BOTH uppercase letters AND numbers together.
 * Regular URL slug words are typically all lowercase (e.g., "clients", "video-sessions").
 */
export const isLikelyBase64Id = (str: string): boolean => {
  const hasUpperCase = /[A-Z]/.test(str);
  const hasNumbers = /[0-9]/.test(str);

  return hasUpperCase && hasNumbers;
};

/**
 * Capitalizes a word if it's a regular word, but leaves base64 IDs unchanged.
 */
export const capitalizeWordIfNotId = (word: string): string => {
  if (isLikelyBase64Id(word)) {
    return word;
  }
  return capitalizeString(word);
};

export const pathNameToBreadCrumbLinks = (pathname: string): BreadcrumbLink[] => {
  const links = pathname.slice(1).split("/");

  const linksToRender = links.length > 2 ? links.slice(0, 2) : links;

  return linksToRender.map((link, index) => {
    const decodedLink = decodeURI(link);
    const formattedName = decodedLink
      .split("-")
      .map((word) => capitalizeWordIfNotId(word))
      .join(" ");

    return {
      name: formattedName,
      current: link === links[links.length - 1],
      type: "component",
      Component: <Link to={`/${links.slice(0, index + 1).join("/")}`}>{formattedName}</Link>,
    };
  });
};

export const formatPlayerTime = (startSeconds: number): string => {
  const hours = Math.floor(startSeconds / 3600);
  const minutes = Math.floor((startSeconds % 3600) / 60);
  const seconds = Math.floor(startSeconds % 60);

  // Pad with leading zeros if necessary
  const hoursStr = hours.toString().padStart(2, "0");
  const minutesStr = minutes.toString().padStart(2, "0");
  const secondsStr = seconds.toString().padStart(2, "0");

  return `${hoursStr}:${minutesStr}:${secondsStr}`;
};

export const getIdList = (items: MultiSelectItem[]): string[] => {
  return items
    .map((item) => {
      if (item.id) {
        return item.id;
      } else if (item.primary) {
        return item.primary
          .toLowerCase() // convert to lowercase
          .replace(/ /g, "-") // replace spaces with hyphens
          .replace(/[^a-z0-9-]/g, ""); // remove non-alphanumeric characters except hyphens
      } else {
        return "";
      }
    })
    .filter((id) => id !== ""); // filter out any empty strings
};

export const getOptionValueList = (items: MultiSelectItem[]): string[] =>
  items.map((item) => item.primary).filter(Boolean);

export const matchOrCreateItems = (strings: string[], matchArray: MultiSelectItem[]): MultiSelectItem[] => {
  return strings.map((str) => {
    const match = matchArray.find((item) => item.id === str || item.primary.toLowerCase() === str.toLowerCase());
    if (match) {
      return match;
    } else {
      return {
        id: str,
        primary: str,
      };
    }
  });
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

export const customClient = async ({
  token,
  document,
  variables,
}: {
  token: string;
  document: RequestDocument;
  variables: Record<string, unknown>; // Accepts any set of variables
}) => {
  const authHeaders: HeadersInit = new Headers();
  authHeaders.append("Authorization", `Bearer ${token}`);
  const headers = convertHeadersToObject(authHeaders);
  const response = await FronteraGraphQLClient.request(document, variables, headers);
  return response;
};

export const downloadFileFromUrl = async ({ url, fileName }: { url: string; fileName: string }) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke object URL to free memory
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("File download failed:", error);
    throw error;
  }
};

export const downloadLearnerFileById = async ({ token, fileId }: { token: string; fileId: string }) => {
  try {
    const response = await queryClient.fetchQuery({
      queryKey: ["get-learner-file-by-id", fileId],
      queryFn: () => customClient({ token, document: LearnerFileByIdDocument, variables: { fileId } }),
    });

    const fileData = response.learnerFileById;

    if (fileData?.signedS3LearnerFile) {
      await downloadFileFromUrl({
        url: fileData.signedS3LearnerFile,
        fileName: fileData.originalFileName,
      });
    } else {
      console.error("No valid file data found.");
    }
  } catch (error) {
    console.error("File download failed:", error);
  }
};

export interface TemplateSelectItem extends SelectItem {
  isBaseTemplate?: boolean;
  payorType?: ApiReportTemplatePayorTypeChoices | null;
}

function mapTemplateEdgeToSelectItem(edge: ReportTemplateTypeEdge): TemplateSelectItem {
  const node = edge?.node;
  const templateId = node?.id;
  const templateName = node?.name ?? "";
  const isBaseTemplate = node?.isBaseTemplate;
  const isDraft = node?.status === ApiReportTemplateStatusChoices.Draft;

  let secondary: string | undefined;
  if (isBaseTemplate && isDraft) {
    secondary = "Frontera • Draft";
  } else if (isBaseTemplate) {
    secondary = "Frontera";
  } else if (isDraft) {
    secondary = "Draft";
  }

  return {
    id: templateId ?? "",
    primary: templateName,
    secondary,
    isBaseTemplate: isBaseTemplate ?? false,
    payorType: node?.payorType ?? null,
  };
}

function compareTemplatesByName(a: ReportTemplateTypeEdge, b: ReportTemplateTypeEdge): number {
  return (a?.node?.name ?? "").localeCompare(b?.node?.name ?? "", undefined, { sensitivity: "base", numeric: true });
}

function buildTemplateSelectItemsCore(
  templateEdges: ReportTemplateTypeEdge[] | null | undefined,
  filterBaseTemplates: boolean,
  canSeeBaseTemplates?: boolean,
): TemplateSelectItem[] {
  if (!templateEdges?.length) return [];

  let templatesToShow = templateEdges;

  if (filterBaseTemplates && !canSeeBaseTemplates) {
    const orgTemplates = templateEdges.filter((edge) => edge?.node?.isBaseTemplate !== true);
    if (orgTemplates.length > 0) {
      templatesToShow = orgTemplates;
    }
  }

  return [...templatesToShow].sort(compareTemplatesByName).map(mapTemplateEdgeToSelectItem);
}

export function buildTemplateSelectItems(
  templateEdges: ReportTemplateTypeEdge[] | null | undefined,
  canSeeBaseTemplates?: boolean,
): TemplateSelectItem[] {
  return buildTemplateSelectItemsCore(templateEdges, true, canSeeBaseTemplates);
}

export function buildTemplateSelectItemsUnfiltered(
  templateEdges: ReportTemplateTypeEdge[] | null | undefined,
): TemplateSelectItem[] {
  return buildTemplateSelectItemsCore(templateEdges, false);
}

export function getSmartDefaultTemplate(templates: TemplateSelectItem[]): TemplateSelectItem | null {
  return templates.length === 1 ? templates[0] : null;
}

/**
 * Derives "City, State" from caregiver metadata.
 * Uses city + state when present; otherwise parses the zip field
 * as pipe-separated "Key: Value" pairs (e.g. "City: Denver|State: CO").
 */
export const formatCaregiverLocation = (metadata: {
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): string => {
  const city = metadata.city?.trim();
  const state = metadata.state?.trim();
  if (city && state) return `${city}, ${state}`;

  const zip = metadata.zip?.trim();
  if (zip) {
    const parts: Record<string, string> = {};
    for (const p of zip.split("|")) {
      const sections = p.trim().split(":", 2);
      if (sections.length === 2) {
        parts[sections[0].trim()] = sections[1].trim();
      }
    }
    const parsedCity = parts["City"]?.trim() ?? "";
    const parsedState = parts["State"]?.trim() ?? "";
    if (parsedCity && parsedState) return `${parsedCity}, ${parsedState}`;
  }

  return "";
};
